import { FC, useState, useMemo, useEffect } from 'react';
import {
  Drawer,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Button
} from '@mui/material';
import { VesselInterface } from '../../utils/types/vessel';
import InputTextSearch from '../form/InputTextSearch';
import './AssignVesselsModal.scss';

interface AssignVesselsModalProps {
  open: boolean;
  onClose: () => void;
  allVessels: VesselInterface[];
  selectedVessels: VesselInterface[];
  onConfirm: (selected: VesselInterface[]) => void;
}

type TabType = 'All' | 'Assigned' | 'Unassigned';

const AssignVesselsModal: FC<AssignVesselsModalProps> = ({
  open,
  onClose,
  allVessels,
  selectedVessels,
  onConfirm
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('All');
  const [currentSelection, setCurrentSelection] = useState<VesselInterface[]>(selectedVessels);

  useEffect(() => {
    if (open) {
      setCurrentSelection(selectedVessels);
    }
  }, [open, selectedVessels]);

  const filteredVessels = useMemo(() => {
    let result = [...allVessels];

    // Search filter
    if (searchTerm) {
      result = result.filter((v) => v.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    // Tab filter
    if (activeTab === 'Assigned') {
      result = result.filter((v) => currentSelection.some((s) => s.id === v.id));
    } else if (activeTab === 'Unassigned') {
      result = result.filter((v) => !currentSelection.some((s) => s.id === v.id));
    }

    return result;
  }, [allVessels, searchTerm, activeTab, currentSelection]);

  const handleToggleVessel = (vessel: VesselInterface) => {
    const isSelected = currentSelection.some((s) => s.id === vessel.id);
    if (isSelected) {
      setCurrentSelection(currentSelection.filter((s) => s.id !== vessel.id));
    } else {
      setCurrentSelection([...currentSelection, vessel]);
    }
  };

  const handleSelectAll = () => {
    const newSelection = [...currentSelection];
    filteredVessels.forEach((v) => {
      if (!newSelection.some((s) => s.id === v.id)) {
        newSelection.push(v);
      }
    });
    setCurrentSelection(newSelection);
  };

  const handleDeselectAll = () => {
    const newSelection = currentSelection.filter(
      (s) => !filteredVessels.some((v) => v.id === s.id)
    );
    setCurrentSelection(newSelection);
  };

  const handleConfirm = () => {
    onConfirm(currentSelection);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="assign-vessels-drawer"
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '400px' },
          padding: '24px',
          boxSizing: 'border-box'
        }
      }}
    >
      <Box className="drawer-content">
        <Typography className="drawer-title">Assign Vessels</Typography>
        
        <Box className="search-container">
          <InputTextSearch
            value={searchTerm}
            placeholder="Search"
            onChange={(e) => setSearchTerm(e.target.value)}
            iconPosition="end"
          />
        </Box>

        <Typography className="summary-text">
          {currentSelection.length} of {allVessels.length} selected
        </Typography>

        <Box className="tabs-container">
          {(['All', 'Assigned', 'Unassigned'] as TabType[]).map((tab) => (
            <Button
              key={tab}
              className={`tab-button ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}>
              {tab}
            </Button>
          ))}
        </Box>

        <FormGroup className="vessels-list" sx={{ flexWrap: 'nowrap' }}>
          {filteredVessels.map((vessel) => (
            <FormControlLabel
              key={vessel.id}
              className="vessel-item"
              sx={{ width: '100%', margin: 0 }}
              control={
                <Checkbox
                  checked={currentSelection.some((s) => s.id === vessel.id)}
                  onChange={() => handleToggleVessel(vessel)}
                  sx={{ color: '#273f82', '&.Mui-checked': { color: '#273f82' } }}
                />
              }
              label={vessel.name}
            />
          ))}
        </FormGroup>

        <Stack direction="row" className="selection-actions">
          <Button className="action-button" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button className="action-button" onClick={handleDeselectAll}>
            Deselect All
          </Button>
        </Stack>

        <Box className="drawer-footer">
          <Button className="confirm-button" onClick={handleConfirm} variant="contained">
            confirm selection
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AssignVesselsModal;
